import React, { useEffect, useMemo, useState } from 'react';
import api from './api';

const PAGE_SIZE = 10;

export default function ProfilePage({ user, onBack }) {
  const isExternal = Boolean(user?.isExternal);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState({
    matches: { online: [] },
    onlineStats: { wins: 0, losses: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const { body, documentElement } = document;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: documentElement.style.overflow,
      htmlHeight: documentElement.style.height,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow || '',
      rootHeight: root?.style.height || '',
    };

    documentElement.style.overflow = 'auto';
    documentElement.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.position = 'static';
    body.style.height = 'auto';
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
    }

    return () => {
      documentElement.style.overflow = prev.htmlOverflow;
      documentElement.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const matchesRes = await api.get('/matches/mine');
        const payload = matchesRes.data?.data || {};
        setData({
          matches: { online: payload.matches?.online || [] },
          onlineStats: payload.onlineStats || { wins: 0, losses: 0, total: 0 },
        });
      } catch (_err) {
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const items = useMemo(() => data.matches?.online || [], [data]);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [currentPage, items]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.headerCard}>
          <div>
            <div style={styles.eyebrow}>{isExternal ? 'Online Match Records' : 'Player Dashboard'}</div>
            <h1 style={styles.h1}>{isExternal ? 'Match History' : 'Player Profile'}</h1>
            <div style={styles.sub}>
              {user?.name}
              {user?.email ? ` · ${user.email}` : ''}
            </div>
          </div>
          <button type="button" onClick={onBack} style={styles.backBtn}>Back</button>
        </div>

        <div style={styles.statsGrid}>
          <StatCard title={isExternal ? 'Imported Coins' : 'Current Coins'} value={user?.coins || 0} tone="green" />
          <StatCard title="Online Wins" value={data.onlineStats?.wins || 0} tone="gold" />
          <StatCard title="Online Losses" value={data.onlineStats?.losses || 0} tone="red" />
          <StatCard title="Total Online Matches" value={data.onlineStats?.total || 0} tone="brown" />
        </div>

        <div style={styles.historyCard}>
          <div style={styles.historyHeader}>
            <div>
              <div style={styles.historyTitle}>Online Match History</div>
              <div style={styles.historySub}>All online records with compact pagination and full match details.</div>
            </div>
            <div style={styles.historyMeta}>{items.length} rows</div>
          </div>

          {loading && <div style={styles.empty}>Loading...</div>}
          {error && <div style={{ ...styles.empty, color: '#ffb0b0' }}>{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div style={styles.empty}>No online matches yet.</div>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date Time</th>
                      <th style={styles.th}>Opponent</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Result</th>
                      <th style={styles.th}>Stake</th>
                      <th style={styles.th}>Score</th>
                      <th style={styles.th}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.map((match) => {
                      const result = getResult(match, user);
                      const opponent = getOpponent(match, user);
                      const typeLabel = getTypeLabel(match);
                      const score = getScoreLine(match, user);
                      return (
                        <tr key={match._id}>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{new Date(match.createdAt).toLocaleString()}</div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{opponent.name}</div>
                            {opponent.sub ? <div style={styles.secondaryText}>{opponent.sub}</div> : null}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{typeLabel}</div>
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.badge,
                                ...(result === 'Win'
                                  ? styles.badgeWin
                                  : result === 'Loss'
                                    ? styles.badgeLoss
                                    : styles.badgeDraw),
                              }}
                            >
                              {result}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{Number(match.stake || 0).toLocaleString()} coins</div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{score}</div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.primaryText}>{formatReason(match.reason)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={styles.paginationRow}>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage <= 1}
                  style={{ ...styles.pageBtn, ...(currentPage <= 1 ? styles.pageBtnDisabled : {}) }}
                >
                  Previous
                </button>
                <div style={styles.pageLabel}>
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage >= totalPages}
                  style={{ ...styles.pageBtn, ...(currentPage >= totalPages ? styles.pageBtnDisabled : {}) }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getResult(match, user) {
  const myId = user?.id;
  if (match.winnerId && String(match.winnerId) === String(myId)) return 'Win';
  if (match.winnerExternalPlayerId && String(match.winnerExternalPlayerId) === String(myId)) return 'Win';
  if (match.loserId && String(match.loserId) === String(myId)) return 'Loss';
  if (match.loserExternalPlayerId && String(match.loserExternalPlayerId) === String(myId)) return 'Loss';
  return 'Draw';
}

function getOpponent(match, user) {
  const snapshots = Array.isArray(match.participantSnapshots) ? match.participantSnapshots : [];
  const myId = user?.id;
  const me = snapshots.find((entry) => {
    if (entry.userId && String(entry.userId) === String(myId)) return true;
    if (user?.isExternal && entry.externalPlayerId && String(entry.externalPlayerId) === String(myId)) return true;
    return false;
  });
  const opponent = snapshots.find((entry) => entry !== me) || null;
  return {
    name: opponent?.name || 'Unknown Opponent',
    sub: opponent?.email || '',
  };
}

function getTypeLabel(match) {
  const targetWins = Number(match.metadata?.sessionTargetWins || 1);
  if (targetWins === 1) return 'One Win';
  if (targetWins === 3) return 'Three Win';
  if (targetWins === 5) return 'Five Win';
  if (targetWins === 7) return 'Seven Win';
  return `First to ${targetWins}`;
}

function getScoreLine(match, user) {
  const sessionWins = match.metadata?.sessionWins || {};
  const whiteWins = Number(sessionWins.white || 0);
  const blackWins = Number(sessionWins.black || 0);
  const snapshots = Array.isArray(match.participantSnapshots) ? match.participantSnapshots : [];
  const first = snapshots[0] || null;
  const second = snapshots[1] || null;
  const isFirstPlayer = first?.userId && String(first.userId) === String(user?.id);
  if (!first || !second) return `${whiteWins}-${blackWins}`;
  return isFirstPlayer ? `${whiteWins}-${blackWins}` : `${blackWins}-${whiteWins}`;
}

function formatReason(reason) {
  return String(reason || 'completed').replace(/-/g, ' ');
}

function StatCard({ title, value, tone }) {
  const toneMap = {
    green: '#40612f',
    gold: '#8f6126',
    red: '#7b3a34',
    brown: '#6b4525',
  };

  return (
    <div style={{ ...styles.statCard, borderColor: toneMap[tone] || toneMap.brown }}>
      <div style={styles.statTitle}>{title}</div>
      <div style={styles.statValue}>{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 72%)',
    padding: '18px 18px 28px',
    overflowY: 'auto',
  },
  container: {
    maxWidth: 1240,
    margin: '0 auto',
    display: 'grid',
    gap: 14,
    paddingBottom: 32,
  },
  headerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    background: 'rgba(36,19,10,0.95)',
    border: '1px solid rgba(215,177,106,0.22)',
    boxShadow: '0 16px 36px rgba(0,0,0,0.2)',
    flexWrap: 'wrap',
  },
  eyebrow: {
    color: '#d8c29a',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.3,
    fontWeight: 800,
  },
  h1: {
    margin: '4px 0 0',
    color: '#f6e7c1',
    fontSize: 30,
  },
  sub: {
    color: '#d8c29a',
    marginTop: 6,
    fontSize: 14,
    wordBreak: 'break-word',
  },
  backBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: 'rgba(36,19,10,0.95)',
    border: '1px solid',
    borderRadius: 16,
    padding: 14,
    color: '#f6e7c1',
    boxShadow: '0 14px 28px rgba(0,0,0,0.18)',
  },
  statTitle: {
    color: '#d8c29a',
    fontSize: 12,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 800,
  },
  historyCard: {
    background: 'rgba(36,19,10,0.95)',
    border: '1px solid rgba(215,177,106,0.28)',
    borderRadius: 20,
    padding: 16,
    display: 'grid',
    gap: 14,
    boxShadow: '0 18px 38px rgba(0,0,0,0.18)',
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
  },
  historyTitle: {
    color: '#ffd48a',
    fontSize: 20,
    fontWeight: 800,
  },
  historySub: {
    color: '#d8c29a',
    fontSize: 13,
    marginTop: 4,
  },
  historyMeta: {
    borderRadius: 999,
    padding: '8px 12px',
    background: 'rgba(215,177,106,0.12)',
    border: '1px solid rgba(215,177,106,0.2)',
    color: '#f6e7c1',
    fontWeight: 700,
    fontSize: 13,
  },
  tableWrap: {
    overflowX: 'auto',
    border: '1px solid rgba(215,177,106,0.16)',
    borderRadius: 16,
    background: 'rgba(29,15,8,0.9)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 980,
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    color: '#ffd48a',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid rgba(215,177,106,0.15)',
    background: 'rgba(36,19,10,0.98)',
  },
  td: {
    padding: '11px 14px',
    borderBottom: '1px solid rgba(215,177,106,0.1)',
    color: '#f6e7c1',
    verticalAlign: 'top',
  },
  primaryText: {
    color: '#f6e7c1',
    fontSize: 14,
    lineHeight: 1.25,
  },
  secondaryText: {
    color: '#d8c29a',
    fontSize: 12,
    marginTop: 3,
    lineHeight: 1.3,
  },
  badge: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: 999,
    border: '1px solid rgba(215,177,106,0.26)',
    color: '#f6e7c1',
    fontSize: 12,
    fontWeight: 700,
  },
  badgeWin: {
    background: '#7a4c22',
  },
  badgeLoss: {
    background: '#643030',
  },
  badgeDraw: {
    background: '#77572b',
  },
  paginationRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  pageBtn: {
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.25)',
    background: 'rgba(58,35,20,0.96)',
    color: '#f6e7c1',
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  pageBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  pageLabel: {
    color: '#d8c29a',
    fontSize: 13,
  },
  empty: {
    padding: 18,
    color: '#d8c29a',
  },
};
